import { Link } from '@tanstack/react-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EditModal, SummaryCard, useEditModal } from '@nucleus-commerce/admin-kit/forms';
import type { Customization } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { FormField, FormItem, FormMessage } from '@nucleus-commerce/ui/components/form';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useCustomizations, useSizeSets } from '@/features/dictionary';
import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import { useProductScope } from '../hooks/use-product-scope';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { CardEditAction, SharedChip } from './product-scope';

/** "+ $45.00"; a delta below zero already carries its sign. Empty when it adds nothing. */
function usePriceDelta() {
  const { t } = useTranslation('products');
  const format = useFormat();
  return (cents: number) => {
    if (cents === 0) return '';
    const price = format.money({ amount: cents, currency: BASE_CURRENCY });
    return cents > 0 ? t('rail.customizations.delta', { price }) : price;
  };
}

/** Which order-line inputs (ring size, engraving, a file) shoppers fill in for this product. */
export function CustomizationsSection() {
  const { t } = useTranslation(['products', 'dictionary']);
  const { readOnly } = useProductScope();
  const form = useFormContext<ProductFormInput>();
  const edit = useEditModal({ form, fields: ['customizationIds'] });
  const selected = new Set(useWatch({ control: form.control, name: 'customizationIds' }));
  // Before the first save nothing picked means the product type's defaults apply (see the mapper).
  const creating = useWatch({ control: form.control, name: 'basePrice' }) !== undefined;
  const productTypeId = useWatch({ control: form.control, name: 'productTypeId' });
  const query = useCustomizations();
  const priceDelta = usePriceDelta();
  const rows = [...(query.data ?? [])].sort((a, b) => a.position - b.position);
  const offered = rows.filter((row) => selected.has(row.id));
  const title = t('products:sections.customizations');

  return (
    <SummaryCard
      title={title}
      badge={<SharedChip />}
      action={
        <CardEditAction
          onEdit={edit.show}
          label={
            <>
              {t('products:scope.edit')} <span className="sr-only">{title}</span>
            </>
          }
        />
      }
    >
      {query.isPending ? (
        <Skeleton className="h-5" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : offered.length === 0 ? (
        <span className="text-muted-foreground text-[13px]">
          {creating && productTypeId
            ? t('products:optionsCard.customizationsFromType')
            : t('products:rail.customizations.none')}
        </span>
      ) : (
        <ul className="flex flex-col gap-1.5" aria-label={title}>
          {offered.map((row) => (
            <li key={row.id} className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate">{row.label}</span>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {[
                  row.isRequired ? t('dictionary:required.yes') : '',
                  priceDelta(row.priceDeltaCents),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      {readOnly ? null : (
        <EditModal
          {...edit.modal}
          size="md"
          title={title}
          description={t('products:rail.customizations.description')}
          footerStart={
            <Link to="/org/customizations" className="text-primary text-[13px] hover:underline">
              {t('products:rail.customizations.manage')}
            </Link>
          }
        >
          <CustomizationChecklist rows={rows} query={query} />
        </EditModal>
      )}
    </SummaryCard>
  );
}

interface CustomizationChecklistProps {
  rows: readonly Customization[];
  query: ReturnType<typeof useCustomizations>;
}

/** Choose which ones this product offers; what each one is stays in Catalog setup. */
function CustomizationChecklist({ rows, query }: CustomizationChecklistProps) {
  const { t } = useTranslation(['products', 'dictionary']);
  const form = useFormContext<ProductFormInput>();
  const details = useCustomizationDetails();

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  }
  if (rows.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('products:customizations.empty')}</p>;
  }
  return (
    <FormField
      control={form.control}
      name="customizationIds"
      render={({ field }) => {
        const selected = new Set(field.value);
        const toggle = (id: string, checked: boolean) => {
          const next = new Set(selected);
          if (checked) next.add(id);
          else next.delete(id);
          // Dictionary order, so the list reads the same everywhere.
          field.onChange(rows.filter((row) => next.has(row.id)).map((row) => row.id));
        };
        return (
          <FormItem>
            <ul
              className="flex flex-col gap-2.5"
              aria-label={t('products:sections.customizations')}
            >
              {rows.map((row) => {
                const on = selected.has(row.id);
                return (
                  <li key={row.id}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3.5 py-3',
                        on && 'border-primary/60',
                      )}
                    >
                      <Checkbox
                        aria-label={t('products:rail.customizations.offer', { label: row.label })}
                        checked={on}
                        onCheckedChange={(checked) => toggle(row.id, checked === true)}
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm font-medium">{row.label}</span>
                        <span className="text-muted-foreground text-xs">{details(row)}</span>
                      </span>
                      {row.isRequired ? (
                        <span className="bg-primary-tint text-primary-tint-foreground shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold">
                          {t('dictionary:required.yes')}
                        </span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

/** "Text · up to 20 characters · + $45.00": a customization's read-only details on one line. */
function useCustomizationDetails() {
  const { t } = useTranslation('dictionary');
  // Here rather than on the card: the page only needs size sets once the modal is open.
  const sizeSets = useSizeSets();
  const priceDelta = usePriceDelta();
  return (row: Customization) => {
    const sizeSet = row.sizeSetId
      ? sizeSets.data?.find((candidate) => candidate.id === row.sizeSetId)
      : undefined;
    return [
      t(`inputType.${row.inputType}`),
      sizeSet?.name ?? '',
      row.inputType === 'text' && row.maxLength !== null
        ? t('rules.maxLength', { count: row.maxLength })
        : '',
      priceDelta(row.priceDeltaCents),
    ]
      .filter(Boolean)
      .join(' · ');
  };
}
